import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

// إنشاء سياق لوحة التحكم
const DashboardContext = createContext(null);

export const DashboardProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create authenticated axios instance
  const getAuthenticatedAxios = useCallback(() => {
    const axiosInstance = axios.create();
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return axiosInstance;
  }, [token]);

  // دالة لجلب بيانات لوحة التحكم مع دعم إلغاء الطلبات
  const fetchDashboardData = useCallback(async (signal) => {
    if (!user) return;

    try {
      setLoading(true);
      const axiosAuth = getAuthenticatedAxios();

      // إضافة signal للطلب لدعم الإلغاء
      const response = await axiosAuth.get('/api/dashboard', {
        signal: signal
      });

      setDashboardData(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      // تجاهل الأخطاء إذا تم إلغاء الطلب
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }

      setError('فشل في تحميل بيانات لوحة التحكم اعد تحميل الصفحة');
      console.error('Dashboard data error:', err);
    } finally {
      setLoading(false);
    }
  }, [user, getAuthenticatedAxios]);

  // تحديث بيانات لوحة التحكم عند تغيير المستخدم أو التوكن مع دعم إلغاء الطلبات
  useEffect(() => {
    const abortController = new AbortController();

    if (user) {
      fetchDashboardData(abortController.signal);
    } else {
      setDashboardData(null);
    }

    // تنظيف عند تغيير التبعيات
    return () => {
      abortController.abort();
    };
  }, [user, token, fetchDashboardData]);

  // دالة لجلب قائمة صفحات الهبوط مع دعم إلغاء الطلبات
  const fetchLandingPages = useCallback(async (signal) => {
    try {
      const axiosAuth = getAuthenticatedAxios();
      const response = await axiosAuth.get('/api/user/landing-pages', {
        signal: signal
      });
      return { success: true, data: response.data };
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return { success: false, cancelled: true };
      }
      return {
        success: false,
        error: 'فشل في تحميل صفحات الهبوط'
      };
    }
  }, [getAuthenticatedAxios]);

  // دالة لجلب قائمة المنتجات مع دعم إلغاء الطلبات
  const fetchProducts = useCallback(async (signal) => {
    try {
      const axiosAuth = getAuthenticatedAxios();
      const response = await axiosAuth.get('/api/user/products', {
        signal: signal
      });
      return { success: true, data: response.data };
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return { success: false, cancelled: true };
      }
      return {
        success: false,
        error: 'فشل في تحميل المنتجات'
      };
    }
  }, [getAuthenticatedAxios]);

  // دالة لجلب قائمة الطلبات مع دعم إلغاء الطلبات
  const fetchOrders = useCallback(async (signal, page = 1, pageSize = 20) => {
    try {
      const axiosAuth = getAuthenticatedAxios();
      const response = await axiosAuth.get('/api/user/orders', {
        signal: signal,
        params: {
          page: page,
          page_size: pageSize,
          include_session_data: false,
          include_geoip: false,
          include_device_detection: false
        }
      });
      return { success: true, data: response.data };
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return { success: false, cancelled: true };
      }
      return {
        success: false,
        error: 'فشل في تحميل الطلبات'
      };
    }
  }, [getAuthenticatedAxios]);

  // دالة لجلب بيانات تحليلية مع دعم إلغاء الطلبات
  const fetchAnalytics = useCallback(async (signal) => {
    try {
      const axiosAuth = getAuthenticatedAxios();
      const response = await axiosAuth.get('/api/user/analytics', {
        signal: signal
      });
      return { success: true, data: response.data };
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return { success: false, cancelled: true };
      }
      return {
        success: false,
        error: 'فشل في تحميل البيانات التحليلية'
      };
    }
  }, [getAuthenticatedAxios]);

  // القيم التي سيتم توفيرها عبر السياق
  const contextValue = useMemo(() => ({
    dashboardData,
    loading,
    error,
    fetchDashboardData,
    fetchLandingPages,
    fetchProducts,
    fetchOrders,
    fetchAnalytics
  }), [
    dashboardData,
    loading,
    error,
    fetchDashboardData,
    fetchLandingPages,
    fetchProducts,
    fetchOrders,
    fetchAnalytics
  ]);

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

// دالة Hook لسهولة استخدام سياق لوحة التحكم
export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};
